import i18n from "@/i18n";
/**
 * VuosilomaYears — VUOSILOMA-V1. Per-holiday-year balances plus the
 * monthly earning status. Display only; all values from the backend.
 */
import {
    qualifyingText, creditYearLabel, creditYearRange, monthLabel, vlDays, vlMonthStatus,
    vlBasisLines, vlFamilyLines, vlReasonText, vlReasonDetailText, vlTsLines, VL_EARNED_STATUS,
} from "./vuosilomaHelpers";

const TONE = {
    ok: "bg-emerald-50 text-emerald-800 border-emerald-200",
    warn: "bg-amber-50 text-amber-900 border-amber-200",
    info: "bg-sky-50 text-sky-900 border-sky-200",
    muted: "bg-neutral-50 text-neutral-600 border-neutral-200",
};

function Cell({ label, value, testId, strong }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wide text-neutral-400">{label}</div>
            <div data-testid={testId}
                className={`tabular-nums ${strong ? "font-semibold text-neutral-900" : "text-neutral-800"}`}>
                {value}
            </div>
        </div>
    );
}

function baseValue(y) {
    if (y.source === "OPENING_BALANCE") return vlDays(y.opening_days);
    if (y.earned_days !== null && y.earned_days !== undefined) return vlDays(y.earned_days);
    if (y.known_min_days > 0) return `at least ${vlDays(y.known_min_days)}`;
    return i18n.t("wf.vuosilomaYears.notKnownYet", "Not known yet");
}

function remainingValue(y, key) {
    if (y[key] !== null && y[key] !== undefined) return vlDays(y[key]);
    return i18n.t("wf.vuosilomaYears.notKnownYet", "Not known yet");
}

export function MonthRow({ m }) {
    const st = vlMonthStatus(m.status);
    return (
        <li data-testid={`vl-month-${m.month}`} className="py-2 text-sm">
            <div className="flex items-center justify-between gap-2">
                <span className="text-neutral-700">{monthLabel(m.month)}</span>
                <span data-testid={`vl-month-status-${m.month}`}
                    className={`rounded-full border px-2 py-0.5 text-xs ${TONE[st.tone]}`}>
                    {st.label}{m.final ? " · final" : ""}
                </span>
            </div>
            {m.qualifying && (
                <p className="mt-1 text-xs text-neutral-600" data-testid={`vl-month-qualifying-${m.month}`}>
                    {qualifyingText(m.qualifying, m.basis)}
                </p>
            )}
            {vlFamilyLines(m.qualifying).map((t, i) => (
                <p key={t} className="mt-0.5 pl-3 text-xs text-neutral-600"
                    data-testid={`vl-month-family-${m.month}-${i}`}>{t}</p>
            ))}
            {vlBasisLines(m.qualifying).map((t, i) => (
                <p key={t} className="mt-0.5 pl-3 text-xs text-neutral-600"
                    data-testid={`vl-month-basis-${m.month}-${i}`}>{t}</p>
            ))}
            {vlTsLines(m.qualifying).map((t, i) => (
                <p key={t} className="mt-0.5 pl-3 text-xs text-neutral-600"
                    data-testid={`vl-month-ts-${m.month}-${i}`}>{t}</p>
            ))}
            {(m.reasons || []).map((r) => (
                <p key={r} className="mt-1 text-xs text-neutral-500"
                    data-testid={`vl-month-reason-${m.month}-${r}`}>{vlReasonText(r)}</p>
            ))}
            {(m.reason_details || []).map((d, i) => (
                <p key={`${d.code}-${d.period_id || i}`} className="mt-0.5 pl-3 text-xs text-amber-800"
                    data-testid={`vl-month-detail-${m.month}-${i}`}>{vlReasonDetailText(d)}</p>
            ))}
            {m.provisional && (
                <p className="mt-1 text-xs text-sky-800" data-testid={`vl-month-provisional-${m.month}`}>
                    Provisional estimate (not posted): {m.provisional.estimate === "FULL_IF_CONFIRMED"
                        ? "full month if confirmed at Close" : "below the threshold so far"}
                </p>
            )}
        </li>
    );
}

export function YearCard({ y }) {
    const cy = y.credit_year_start;
    return (
        <div data-testid={`vl-year-${cy}`} className="rounded-md border border-neutral-200 bg-white p-3">
            <div className="flex items-baseline justify-between">
                <span className="text-sm font-semibold text-neutral-900">{creditYearLabel(cy)}</span>
                <span className="text-xs text-neutral-500">{creditYearRange(cy)}</span>
            </div>
            <p className="mt-0.5 text-xs text-neutral-500" data-testid={`vl-year-${cy}-earned-status`}>
                {VL_EARNED_STATUS[y.earned_status] || y.earned_status}
            </p>
            <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-5">
                <Cell label={y.source === "OPENING_BALANCE" ? i18n.t("wf.vuosilomaYears.opening", "Opening") : i18n.t("wf.vuosilomaYears.earned", "Earned")}
                    value={baseValue(y)} testId={`vl-year-${cy}-base`} />
                <Cell label={i18n.t("wf.vuosilomaYears.usedFinal", "Used (final)")} value={vlDays(y.used_days)} testId={`vl-year-${cy}-used`} />
                <Cell label={i18n.t("wf.vuosilomaYears.pending", "Pending")} value={vlDays(y.pending_days)} testId={`vl-year-${cy}-pending`} />
                <Cell label={i18n.t("wf.vuosilomaYears.remaining", "Remaining")} value={remainingValue(y, "remaining_days")}
                    testId={`vl-year-${cy}-remaining`} strong />
                <Cell label={i18n.t("wf.vuosilomaYears.availableToBook", "Available to book")} value={remainingValue(y, "available_days")}
                    testId={`vl-year-${cy}-available`} strong />
            </div>
            {y.opening_full_months !== undefined && y.source !== "OPENING_BALANCE" && (
                <p className="mt-2 text-xs text-neutral-500" data-testid={`vl-year-${cy}-opening-months`}>
                    Full months before opening: {y.opening_full_months ?? "not recorded"}
                    {" · "}known full months: {y.full_months_known}
                </p>
            )}
            {(y.reasons || []).map((r) => (
                <p key={r} className="mt-1 text-xs text-amber-800"
                    data-testid={`vl-year-${cy}-reason-${r}`}>{vlReasonText(r)}</p>
            ))}
            {y.months?.length > 0 && (
                <details className="mt-2" data-testid={`vl-year-${cy}-months`}>
                    <summary className="cursor-pointer text-xs text-neutral-600">{i18n.t("wf.vuosilomaYears.monthlyEarning", "Monthly earning")}</summary>
                    <ul className="divide-y divide-neutral-100">
                        {y.months.map((m) => <MonthRow key={m.month} m={m} />)}
                    </ul>
                </details>
            )}
        </div>
    );
}
